import type { ReactNode } from 'react';

import { twcn } from '@web/ui/core/utils';
import type { ToneChipTone } from './tone_chip';

/**
 * Status badge — the `vj-badge` of the handoff: a tonal pill with a leading
 * solid dot and a mono uppercase label. Distinct from `ToneChip` (the icon
 * chip): the badge carries lifecycle status, the chip carries categorical tags.
 * The tone drives both the tint/ink pill colours and the dot's saturated colour.
 */
const PILL_CLASS: Record<ToneChipTone, string> = {
  green: 'bg-tone-green-tint text-tone-green-ink',
  amber: 'bg-tone-amber-tint text-tone-amber-ink',
  red: 'bg-tone-red-tint text-tone-red-ink',
  gray: 'bg-tone-gray-tint text-tone-gray-ink',
  ai: 'bg-ai-tint text-ai-ink',
  brand: 'bg-primary-tint text-primary-ink',
};

const DOT_CLASS: Record<ToneChipTone, string> = {
  green: 'bg-tone-green-fg',
  amber: 'bg-tone-amber-fg',
  red: 'bg-tone-red-fg',
  gray: 'bg-tone-gray-fg',
  ai: 'bg-ai',
  brand: 'bg-primary',
};

export function StatusBadge({
  tone,
  children,
}: Readonly<{ tone: ToneChipTone; children: ReactNode }>): React.JSX.Element {
  return (
    <span
      className={twcn(
        'inline-flex h-5.5 items-center gap-1.25 whitespace-nowrap rounded-pill px-2.25 font-mono text-tiny font-medium uppercase tracking-eyebrow',
        PILL_CLASS[tone],
      )}
    >
      <span className={twcn('h-dot-xs w-dot-xs rounded-pill', DOT_CLASS[tone])} aria-hidden />
      <span>{children}</span>
    </span>
  );
}
